import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Input, Row } from 'reactstrap'
import { IEvent } from './index'
import { toast } from 'react-toastify'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'
import { Spinner } from 'reactstrap'
import { ISort } from 'lib/type'
import { showDiscountType, formatTime, showBooths } from 'lib/util'

interface IProds {
  fetching: boolean
  eventRecords: IEvent[]
  handleOpenDetails: (arg: string) => void
  setOpenEventPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteEvent: (arg: string) => Promise<string>
  searchEvent: (arg: string) => void
  sortAction: ISort
  setSortAction: (arg: ISort) => void
  setCanEdit: (arg: boolean) => void
}

const EventsTable: React.FC<IProds> = ({
  eventRecords,
  handleOpenDetails,
  setOpenEventPopup,
  setEditMode,
  fetching,
  handleDeleteEvent,
  searchEvent,
  sortAction,
  setSortAction,
  setCanEdit
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [eventSelectedId, setEventSelectedId] = useState<string>()
  const [query, setQuery] = useState('')
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false,
  })

  const handleAddEvent = () => {
    setEditMode(false)
    setOpenEventPopup(true)
  }

  const handleEditEvent = (id: string) => {
    setEditMode(true)
    setOpenEventPopup(true)
    handleOpenDetails(id)
  }

  const handleDeleteEventRecord = async (id: string) => {
    const res = handleDeleteEvent(id)

    const message = await res
    if (message === 'SUCCESSFULL') {
      toast.success('Deleted a Event successfully')
    }
  }

  const handleShowModalDelete = (id: string) => {
    setShowModalDelete(true)
    setEventSelectedId(id)
  }

  const handleDelete = (id: string) => {
    handleDeleteEventRecord(id)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    searchEvent(searchTerm)
  }, 1000)

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleActionSort = (sortBy: string) => {
    if (sortAction.sortBy === sortBy) {
      setSortAction({
        ...sortAction,
        sortDirection: sortAction.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      })
    } else {
      setSortAction({
        sortDirection: 'DESC',
        sortBy: sortBy
      })
    }
  }

  useEffect(() => {
    const userType = sessionStorage.getItem('userType')
    
    // Type 1 = Admin - full access to everything
    if (userType === '1') {
      setPermissions({
        canCreate: true,
        canEdit: true,
        canDelete: true,
      })
      setCanEdit(true)
      return
    }
    
    // Type 2 = Regular user - use role-based permissions
    const userRoles = sessionStorage.getItem('userRoles') ? JSON.parse(atob(sessionStorage.getItem('userRoles')!)) : null
    if (userRoles) {
      userRoles.forEach((role: any) => {
        if (role.page === 'Events') {
          setPermissions({
            canCreate: role.canCreate,
            canEdit: role.canEdit,
            canDelete: role.canDelete,
          })
          setCanEdit(role.canEdit)
        }
      })
    }
  }, [setCanEdit])

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-action-price'
                  className='form-control search'
                  placeholder='Search Events'
                  onChange={(e) => handleInputChange(e)}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              {
                permissions.canCreate &&
                <Button className='p-2' color='primary' onClick={(e) => handleAddEvent()}>
                  <div className='flex align-items-center'>
                    {fetching ? (
                      <Spinner size='sm'> Loading... </Spinner>
                    ) : (
                      <>
                        <i className='mdi mdi-plus'></i>
                        <span className='fw-semibold ms-1'>New Event</span>
                      </>
                    )}
                  </div>
                </Button>
              }
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' onClick={() => handleActionSort('id')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>ID</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'id' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'id' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('name')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Name</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'name' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'name' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('startDateTime')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Start Date Time</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'startDateTime' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'startDateTime' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('finishDateTime')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Finish Date Time</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'finishDateTime' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'finishDateTime' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('discountType')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Discount Type</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'discountType' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'discountType' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('discountValue')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Discount Value</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'discountValue' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'discountValue' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th>Booths</th>
                      <th scope='col'>Active</th>
                      <th scope='col' style={{ width: '150px' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventRecords.map((event, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <Link to='#' className='fw-medium'>
                              {event._id}
                            </Link>
                          </td>
                          <td>{event.name}</td>
                          <td>{event.startDateTime ? formatTime(event.startDateTime) : ''}</td>
                          <td>{event.finishDateTime ? formatTime(event.finishDateTime) : ''}</td>
                          <td>{showDiscountType(event.discountType)}</td>
                          <td>{event.discountValue}</td>
                          <td>{showBooths(event.boothEvents)}</td>
                          <td>
                            <Input
                              checked={event.isActive || false}
                              className='form-check-input code-switcher'
                              type='checkbox'
                            />
                          </td>
                          <td>
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={(e) => handleEditEvent(event?._id as string)}
                            >
                              Details
                            </button>
                            {
                              permissions.canDelete &&
                              <button
                                type='button'
                                className='btn btn-sm btn-danger ms-2'
                                onClick={(e) => handleShowModalDelete(event?._id as string)}
                              >
                                Delete
                              </button>
                            }
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={() => handleDelete(eventSelectedId as string)}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}
export default EventsTable
